import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useI18n } from '../../i18n';
import {
  Upload,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  FileText,
  Trash2,
  RefreshCw,
  Info,
  Clock,
  Database,
  Bug,
  FileWarning,
  ShieldCheck,
  ShieldAlert,
  X
} from 'lucide-react';
import apiClient from '../../api/client';

const FileUploadScanner = ({
  workspaceId = null,
  sourceType = 'upload',
  sourceId = null,
  onScanComplete = null,
  showHistory = true,
  maxFileSize = 100 * 1024 * 1024, // 100 MB
  allowedTypes = null, // null = all types
  compact = false
}) => {
  const { t } = useI18n();
  const [files, setFiles] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [clamavStatus, setClamavStatus] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Load ClamAV status on mount
  useEffect(() => {
    checkClamAVStatus();
    if (showHistory) {
      loadScanHistory();
    }
  }, [showHistory]);

  const checkClamAVStatus = async () => {
    try {
      const response = await apiClient.get('/antivirus/status');
      setClamavStatus(response.data);
    } catch (err) {
      setClamavStatus({ is_available: false, error: err.message });
    }
  };

  const loadScanHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await apiClient.get('/antivirus/scans/history', {
        params: { limit: 10 }
      });
      setScanHistory(response.data.items || []);
    } catch (err) {
      console.error('Error loading scan history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError(null);

    const droppedFiles = [...e.dataTransfer.files];
    validateAndAddFiles(droppedFiles);
  }, []);

  const handleFileSelect = (e) => {
    setError(null);
    const selectedFiles = [...e.target.files];
    validateAndAddFiles(selectedFiles);
    e.target.value = null; // Reset input
  };

  const validateAndAddFiles = (newFiles) => {
    const validFiles = [];
    const errors = [];

    newFiles.forEach(file => {
      // Check size
      if (file.size > maxFileSize) {
        errors.push(`${file.name}: ${t('antivirus.fileTooLarge', 'File too large')} (max ${formatFileSize(maxFileSize)})`);
        return;
      }

      // Check type if restricted
      if (allowedTypes && !allowedTypes.includes(file.type)) {
        errors.push(`${file.name}: ${t('antivirus.typeNotAllowed', 'File type not allowed')}`);
        return;
      }

      validFiles.push({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'pending',
        result: null
      });
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const scanFiles = async () => {
    if (files.length === 0 || !clamavStatus?.is_available) return;

    setScanning(true);
    setError(null);

    const updatedFiles = [...files];

    for (let i = 0; i < updatedFiles.length; i++) {
      if (updatedFiles[i].status !== 'pending') continue;

      updatedFiles[i].status = 'scanning';
      setFiles([...updatedFiles]);

      try {
        const formData = new FormData();
        formData.append('file', updatedFiles[i].file);

        const params = new URLSearchParams();
        if (workspaceId) params.append('workspace_id', workspaceId);
        if (sourceType) params.append('source_type', sourceType);

        const response = await apiClient.post(
          `/antivirus/scan/upload?${params.toString()}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 300000 // 5 minutes for scanning
          }
        );

        updatedFiles[i].status = response.data.status;
        updatedFiles[i].result = response.data;
        setFiles([...updatedFiles]);

        if (onScanComplete) {
          onScanComplete(response.data);
        }

      } catch (err) {
        updatedFiles[i].status = 'error';
        updatedFiles[i].result = {
          error: err.response?.data?.detail || err.message
        };
        setFiles([...updatedFiles]);
      }
    }

    setScanning(false);

    // Refresh history
    if (showHistory) {
      loadScanHistory();
    }
  };

  const clearFiles = () => {
    setFiles([]);
    setError(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'clean':
        return <ShieldCheck className="w-5 h-5 text-green-400" />;
      case 'infected':
        return <ShieldAlert className="w-5 h-5 text-red-400" />;
      case 'scanning':
        return <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status, severity = null) => {
    const baseClasses = "px-2 py-0.5 rounded-full text-xs font-medium";

    switch (status) {
      case 'clean':
        return <span className={`${baseClasses} bg-green-500/20 text-green-400`}>{t('antivirus.clean', 'Clean')}</span>;
      case 'infected':
        const severityColors = {
          critical: 'bg-red-500/20 text-red-400',
          high: 'bg-orange-500/20 text-orange-400',
          medium: 'bg-yellow-500/20 text-yellow-400',
          low: 'bg-blue-500/20 text-blue-400'
        };
        return (
          <span className={`${baseClasses} ${severityColors[severity] || 'bg-red-500/20 text-red-400'}`}>
            {t('antivirus.infected', 'Infected')} {severity && `(${severity})`}
          </span>
        );
      case 'scanning':
        return <span className={`${baseClasses} bg-blue-500/20 text-blue-400`}>{t('antivirus.scanning', 'Scanning...')}</span>;
      case 'error':
        return <span className={`${baseClasses} bg-red-500/20 text-red-400`}>{t('common.error', 'Error')}</span>;
      default:
        return <span className={`${baseClasses} bg-gray-500/20 text-gray-400`}>{t('antivirus.pending', 'Pending')}</span>;
    }
  };

  if (compact) {
    return (
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-5 h-5 text-purple-400" />
          <span className="text-white font-medium">{t('antivirus.scanFile', 'Scan File')}</span>
          {clamavStatus?.is_available && (
            <span className="text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded">
              ClamAV {t('common.ready', 'Ready')}
            </span>
          )}
        </div>

        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
            dragActive ? 'border-purple-500 bg-purple-500/10' : 'border-gray-600 hover:border-gray-500'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-400">{t('antivirus.dropOrClick', 'Drop file or click to select')}</p>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            multiple
          />
        </div>

        {files.length > 0 && (
          <div className="mt-3 space-y-2">
            {files.map((f, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-700/50 rounded p-2">
                <div className="flex items-center gap-2 min-w-0">
                  {getStatusIcon(f.status)}
                  <span className="text-sm text-white truncate">{f.name}</span>
                </div>
                {getStatusBadge(f.status, f.result?.threat_severity)}
              </div>
            ))}
            <button
              onClick={scanFiles}
              disabled={scanning || !clamavStatus?.is_available}
              className="w-full mt-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              {scanning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('antivirus.scanning', 'Scanning...')}
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4" />
                  {t('antivirus.scanNow', 'Scan Now')}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Shield className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">{t('antivirus.title', 'Antivirus Scanner')}</h2>
            <p className="text-sm text-gray-400">{t('antivirus.subtitle', 'Scan files for malware using ClamAV')}</p>
          </div>
        </div>

        {/* ClamAV Status */}
        <div className="flex items-center gap-2">
          {clamavStatus?.is_available ? (
            <div className="flex items-center gap-2 text-green-400 bg-green-500/10 px-3 py-1.5 rounded-lg">
              <CheckCircle className="w-4 h-4" />
              <div className="text-sm">
                <span className="font-medium">ClamAV</span>
                {clamavStatus.signature_count && (
                  <span className="text-gray-400 ml-2">{clamavStatus.signature_count.toLocaleString()} signatures</span>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-red-400 bg-red-500/10 px-3 py-1.5 rounded-lg">
              <XCircle className="w-4 h-4" />
              <span className="text-sm">{t('antivirus.unavailable', 'ClamAV Unavailable')}</span>
            </div>
          )}
          <button
            onClick={checkClamAVStatus}
            className="p-2 text-gray-400 hover:text-white transition-colors"
            title={t('common.refresh', 'Refresh')}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <pre className="text-sm text-red-400 whitespace-pre-wrap">{error}</pre>
        </div>
      )}

      {/* Drop zone */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
          dragActive
            ? 'border-purple-500 bg-purple-500/10'
            : 'border-gray-600 hover:border-gray-500 hover:bg-gray-800/50'
        } ${!clamavStatus?.is_available ? 'opacity-50 cursor-not-allowed' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={clamavStatus?.is_available ? handleDrop : undefined}
        onClick={() => clamavStatus?.is_available && fileInputRef.current?.click()}
      >
        <Upload className={`w-12 h-12 mx-auto mb-4 ${dragActive ? 'text-purple-400' : 'text-gray-400'}`} />
        <p className="text-lg text-white mb-2">
          {t('antivirus.dropFiles', 'Drop files here or click to select')}
        </p>
        <p className="text-sm text-gray-400">
          {t('antivirus.maxSize', 'Maximum file size')}: {formatFileSize(maxFileSize)}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileSelect}
          multiple
          disabled={!clamavStatus?.is_available}
        />
      </div>

      {/* Selected files */}
      {files.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-medium">{t('antivirus.selectedFiles', 'Selected Files')} ({files.length})</h3>
            <button
              onClick={clearFiles}
              disabled={scanning}
              className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" />
              {t('common.clearAll', 'Clear All')}
            </button>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {files.map((f, i) => (
              <div
                key={i}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  f.status === 'infected'
                    ? 'bg-red-500/10 border-red-500/30'
                    : f.status === 'clean'
                      ? 'bg-green-500/10 border-green-500/30'
                      : 'bg-gray-800/50 border-gray-700'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {getStatusIcon(f.status)}
                  <div className="min-w-0 flex-1">
                    <p className="text-white truncate">{f.name}</p>
                    <p className="text-xs text-gray-400">
                      {formatFileSize(f.size)}
                      {f.result?.threat_name && (
                        <span className="text-red-400 ml-2">
                          <Bug className="w-3 h-3 inline mr-1" />
                          {f.result.threat_name}
                        </span>
                      )}
                      {f.result?.scan_time_ms && (
                        <span className="text-gray-500 ml-2">
                          ({f.result.scan_time_ms}ms)
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {getStatusBadge(f.status, f.result?.threat_severity)}
                  {f.status === 'pending' && (
                    <button
                      onClick={() => removeFile(i)}
                      className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Scan button */}
          <div className="mt-4 flex gap-3">
            <button
              onClick={scanFiles}
              disabled={scanning || !clamavStatus?.is_available || files.every(f => f.status !== 'pending')}
              className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              {scanning ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('antivirus.scanning', 'Scanning...')}
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  {t('antivirus.scanAll', 'Scan All Files')}
                </>
              )}
            </button>
          </div>

          {/* Scan summary */}
          {files.some(f => f.status !== 'pending') && (
            <div className="mt-4 p-4 bg-gray-800/50 rounded-lg">
              <h4 className="text-sm font-medium text-white mb-2">{t('antivirus.summary', 'Scan Summary')}</h4>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-white">{files.length}</p>
                  <p className="text-xs text-gray-400">{t('antivirus.total', 'Total')}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">
                    {files.filter(f => f.status === 'clean').length}
                  </p>
                  <p className="text-xs text-gray-400">{t('antivirus.clean', 'Clean')}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-400">
                    {files.filter(f => f.status === 'infected').length}
                  </p>
                  <p className="text-xs text-gray-400">{t('antivirus.infected', 'Infected')}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-400">
                    {files.filter(f => f.status === 'pending').length}
                  </p>
                  <p className="text-xs text-gray-400">{t('antivirus.pending', 'Pending')}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Scan History */}
      {showHistory && scanHistory.length > 0 && (
        <div className="mt-8 border-t border-gray-700 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-medium flex items-center gap-2">
              <Database className="w-5 h-5 text-gray-400" />
              {t('antivirus.recentScans', 'Recent Scans')}
            </h3>
            <button
              onClick={loadScanHistory}
              disabled={loadingHistory}
              className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
            >
              <RefreshCw className={`w-4 h-4 ${loadingHistory ? 'animate-spin' : ''}`} />
              {t('common.refresh', 'Refresh')}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-700">
                  <th className="pb-2 font-medium">{t('antivirus.filename', 'Filename')}</th>
                  <th className="pb-2 font-medium">{t('antivirus.size', 'Size')}</th>
                  <th className="pb-2 font-medium">{t('antivirus.status', 'Status')}</th>
                  <th className="pb-2 font-medium">{t('antivirus.threat', 'Threat')}</th>
                  <th className="pb-2 font-medium">{t('antivirus.scannedAt', 'Scanned')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {scanHistory.map((scan) => (
                  <tr key={scan.id} className="text-gray-300">
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="truncate max-w-[200px]">{scan.original_filename || scan.filename}</span>
                      </div>
                    </td>
                    <td className="py-3 text-gray-400">{formatFileSize(scan.file_size)}</td>
                    <td className="py-3">{getStatusBadge(scan.status, scan.threat_severity)}</td>
                    <td className="py-3">
                      {scan.threat_name ? (
                        <span className="text-red-400 flex items-center gap-1">
                          <Bug className="w-3 h-3" />
                          {scan.threat_name}
                        </span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="py-3 text-gray-400">
                      {new Date(scan.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadScanner;
